import { useState } from 'react';
import { api, errorText } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { eurC } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Sheet } from '../belege/Sheet';

// Von der Kanzlei vorbereitete Umsatzsteuer-Voranmeldung prüfen und selbst senden (server/src/modules/fristen/elster.ts):
// Die Kanzlei darf sie nicht für Sie senden (nur Steuerberater dürfen das), darum senden Sie mit Ihrem eigenen
// ELSTER-Zertifikat aus „Mein ELSTER“. Zertifikat und PIN speichert die App nicht.

interface Item {
  id: string;
  title: string;
  deadline: string;
  kennzahlen: Record<string, number>;
  zahllastCents: number;
}
interface Antwort {
  bereit: boolean;
  grund: string | null;
  test: boolean;
  kz: { text: Record<string, string>; euro: string[]; eingabe: string[] };
  items: Item[];
}

const betrag = (cents: number) => (cents < 0 ? t('Erstattung {amount}', { amount: eurC(-cents / 100) }) : t('Zahllast {amount}', { amount: eurC(cents / 100) }));

export function UstvaPruefenCard({ clientId }: { clientId: string }) {
  const res = useApi<Antwort>(`/api/app/ustva-pruefen/${clientId}`, { live: ['fristen'], clientId });
  const [offen, setOffen] = useState<Item | null>(null);
  const d = res.data;
  if (!d || !d.items.length) return null;
  return (
    <>
      {d.items.map((v) => (
        <div key={v.id} className="card ustva">
          <h3>{t('Umsatzsteuer prüfen und senden')}</h3>
          <div className="gl">{t('Ihre Kanzlei hat die Voranmeldung vorbereitet. Sie senden sie mit Ihrem ELSTER-Zertifikat.')}</div>
          <div className="next first">
            <b>{tx(v.title)}</b> · {betrag(v.zahllastCents)} · {t('Frist {date}', { date: fmtDate(v.deadline) })}
          </div>
          <div className="bwa-row">
            <button type="button" className="btn2 dark" onClick={() => setOffen(v)}>
              {t('Prüfen und senden')}
            </button>
          </div>
        </div>
      ))}
      {offen && (
        <PruefenSheet
          item={offen}
          stand={d}
          onClose={() => setOffen(null)}
          onSent={async () => {
            setOffen(null);
            await res.reload();
          }}
        />
      )}
    </>
  );
}

function PruefenSheet({ item, stand, onClose, onSent }: { item: Item; stand: Antwort; onClose(): void; onSent(): Promise<void> }) {
  const toast = useToast();
  const [zert, setZert] = useState<File | null>(null);
  const [pin, setPin] = useState('');
  const [geprueft, setGeprueft] = useState(false);
  const [busy, setBusy] = useState<'pruefen' | 'senden' | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const euro = new Set(stand.kz.euro);
  const zeilen = [...stand.kz.eingabe, 'Kz83'].filter((kz) => item.kennzahlen[kz] !== undefined);

  async function los(nurPruefen: boolean) {
    setError(null);
    setOk(null);
    if (!nurPruefen && !geprueft) return setError(t('Bitte bestätigen Sie, dass Sie die Zahlen geprüft haben.'));
    if (!nurPruefen && (!zert || !pin)) return setError(t('Bitte Ihre Zertifikatsdatei und die PIN angeben.'));
    const fd = new FormData();
    fd.append('data', JSON.stringify({ nurPruefen }));
    if (zert) fd.append('zertifikat', zert, zert.name);
    fd.append('pin', pin);
    setBusy(nurPruefen ? 'pruefen' : 'senden');
    try {
      const r = await api.post<{ geprueft?: boolean; test?: boolean }>(`/api/app/ustva-pruefen/${item.id}/senden`, fd);
      if (r.geprueft) setOk(t('ELSTER hat die Voranmeldung geprüft: alles in Ordnung.'));
      else {
        toast(r.test ? t('An das Testsystem von ELSTER gesendet') : t('An ELSTER gesendet'));
        await onSent();
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Sheet title={tx(item.title)} onClose={onClose}>
      <div className="meta">{t('Frist {date}', { date: fmtDate(item.deadline) })}</div>
      {!stand.bereit && <p className="formerr">{stand.grund ? tx(stand.grund) : t('Senden geht gerade nicht.')}</p>}
      <dl className="akte ptbl">
        {zeilen.map((kz) => (
          <div key={kz}>
            <dt>
              {kz.slice(2)} · {tx(stand.kz.text[kz] ?? kz)}
            </dt>
            <dd>{euro.has(kz) ? eurC(item.kennzahlen[kz]!).replace(/,00 €$/, ' €') : eurC(item.kennzahlen[kz]! / 100)}</dd>
          </div>
        ))}
      </dl>
      <div className="form maform">
        <div className="fld">
          <label htmlFor="up-zert">
            <span>{t('Ihre ELSTER-Zertifikatsdatei (.pfx)')}</span>
            <input id="up-zert" type="file" accept=".pfx,.p12" onChange={(e) => setZert(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        <div className="fld">
          <label htmlFor="up-pin">
            <span>{t('PIN der Zertifikatsdatei')}</span>
            <input id="up-pin" type="password" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
          </label>
        </div>
        <label className="up-check">
          <input type="checkbox" checked={geprueft} onChange={(e) => setGeprueft(e.target.checked)} />
          <span>{t('Ich habe die Zahlen geprüft.')}</span>
        </label>
        <span className="hint">{t('Datei und PIN nutzt die App nur für dieses Senden und speichert sie nicht.')}</span>
        {ok && <p className="meta up-ok">{ok}</p>}
        {error && (
          <p className="formerr" role="alert">
            {error}
          </p>
        )}
        <div className="row">
          <button type="button" className="btn2 dark" disabled={!!busy || !stand.bereit} onClick={() => void los(false)}>
            {busy === 'senden' ? t('Sendet …') : t('Jetzt senden')}
          </button>
          <button type="button" className="btn2" disabled={!!busy || !stand.bereit} onClick={() => void los(true)}>
            {busy === 'pruefen' ? t('Prüft …') : t('Erst prüfen')}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
